import "./DropdownInput.scss";
import React, { useEffect, useState } from "react";
import { InputError, InputLabel } from "..";
import { InputOptionWithImage, InputProps } from "../../../types/inputs";
import Select, { SingleValue } from "react-select";

export interface DropdownInputProps extends InputProps {
  /**
   * The options to be displayed within dropdown.
   */
  options: InputOptionWithImage[];
  /**
   * Function to be passed in so that the parent component can
   * be notified of any query changes. Query state will still
   * be managed internally. Will be cleared when the value prop
   * is updated.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Disables the internal filtering of options. Perfect for
   * when you want to filter options outside of this component.
   */
  disableFilter?: boolean;
  /**
   * Disables the ability to type in search bar.
   */
  disableSearch?: boolean;
  /**
   * Removes borders and margins so that the component can
   * be used seemlessly inside other components.
   */
  borderless?: boolean;

  isLoading?: boolean;
}

/**
 * Component for selecting options from a dropdown.
 * Wraps the React Select component: https://react-select.com/
 */
const DropdownInput: React.VFC<DropdownInputProps> = ({
  label,
  required,
  options,
  value,
  onChange,
  onQueryChange,
  disableFilter,
  disableSearch,
  error,
  touched,
  borderless,
  isLoading = false,
}) => {
  /**
   * Internal state.
   */
  const [internalQuery, setInternalQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  /**
   * Notify parent of any internal query changes.
   */
  useEffect(() => {
    if (onQueryChange) {
      onQueryChange(internalQuery);
    }
  }, [onQueryChange, internalQuery]);

  /**
   * Clear queries when value is changed/selected;
   */
  useEffect(() => {
    setInternalQuery("");
    if (onQueryChange) {
      onQueryChange("");
    }
  }, [value]);

  /**
   * Function to be called when an option is selected.
   */
  const onOptionSelect = (
    newValue: SingleValue<InputOptionWithImage>
  ): void => {
    if (newValue) {
      onChange(newValue.value);
      setInternalQuery(newValue.label);
    }
  };

  /**
   * Filter out any options that don't match the query value.
   * Does nothing if search is disabled.
   */
  useEffect(() => {
    if (!disableFilter) {
      setFilteredOptions(
        options.filter((option) => {
          const formattedLabel = option.label.trim().toLowerCase();
          const formattedQuery = internalQuery.trim().toLowerCase();
          return formattedLabel.includes(formattedQuery);
        })
      );
    } else {
      setFilteredOptions(options);
    }
  }, [options]);

  const styleModifier = borderless ? "borderless" : "default";

  return (
    <div className={`dropdowninput-${styleModifier}`}>
      <InputLabel label={label} required={required} />
      <Select
        isLoading={isLoading}
        className={`dropdowninput__input`}
        value={{
          label:
            options.find((option) => option.value === value)?.label || value,
          value: value,
        }}
        options={filteredOptions}
        inputValue={internalQuery}
        onInputChange={setInternalQuery}
        onChange={onOptionSelect}
        isSearchable={!disableSearch}
        styles={{
          // Remove default container styles
          control: (provided) => {
            return {
              ...provided,
              border: "none",
              borderRadius: 2,
            };
          },
          // Start: Inject image if option contains image
          input: (provided, state) => {
            const image = options.find(
              (option) => option.value === state.value
            )?.image;
            if (image) {
              return {
                ...provided,
                display: "flex",
              };
            } else {
              return provided;
            }
          },
          singleValue: (provided, state) => {
            const image = options.find(
              (option) => option.value === state.data.value
            )?.image;
            if (image) {
              return {
                ...provided,
                width: 50,
                marginLeft: 50,
                ":before": {
                  content: '""',
                  width: 44,
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: 5,
                  bottom: 0,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                  backgroundImage: `url(${image})`,
                  backgroundPosition: "center",
                },
              };
            } else {
              return provided;
            }
          },
          option: (provided, state) => {
            const image = options.find(
              (o) => o.value === state.data.value
            )?.image;
            const optionStyles = {
              backgroundColor: state.isSelected
                ? "#71cace"
                : state.isFocused
                ? "#e8f7f7"
                : undefined,
            };
            if (image) {
              return {
                ...provided,
                ...optionStyles,
                width: "100%",
                position: "relative",
                paddingLeft: 55,
                ":before": {
                  content: '""',
                  width: 44,
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: 5,
                  bottom: 0,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                  backgroundImage: `url(${image})`,
                  backgroundPosition: "center",
                },
              };
            } else {
              return { ...provided, ...optionStyles };
            }
          },
          // End: Inject image if option contains image
        }}
      />
      <InputError error={error} touched={touched} />
    </div>
  );
};

export default DropdownInput;
